import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, Building2, ShoppingBag, Users, Layers, Search } from 'lucide-react';
import Reveal from '@/components/site/Reveal';
import SectionHeading from '@/components/site/SectionHeading';
import PropertyCard from '@/components/site/PropertyCard';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import api from '@/lib/api';

const CATEGORIES = [
  { key: 'office', label: 'Grade A Office Spaces', icon: Building2, image: 'https://images.unsplash.com/photo-1549757521-4160565ff3de?auto=format&fit=crop&w=2000&q=80' },
  { key: 'retail', label: 'Retail Showrooms', icon: ShoppingBag, image: 'https://images.pexels.com/photos/1313534/pexels-photo-1313534.jpeg' },
  { key: 'coworking', label: 'Coworking Spaces', icon: Users, image: 'https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=2000&q=80' },
  { key: 'mixed-use', label: 'Mixed-Use Developments', icon: Layers, image: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=2000&q=80' },
];

export default function FeaturedProperties() {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ location: '', property_type: 'all', min_area: '', max_budget: '' });
  const navigate = useNavigate();

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    api.get('/properties', { params: { featured: true } })
      .then((res) => { if (!cancel) setProperties(res.data || []); })
      .catch(() => {})
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, []);

  const onSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (filters.location) params.set('location', filters.location);
    if (filters.property_type && filters.property_type !== 'all') params.set('property_type', filters.property_type);
    if (filters.min_area) params.set('min_area', filters.min_area);
    if (filters.max_budget) params.set('max_budget', filters.max_budget);
    navigate(`/properties?${params.toString()}`);
  };

  return (
    <section data-testid="home-featured" className="py-20 md:py-28 bg-slate-50">
      <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
          <Reveal>
            <SectionHeading
              kicker="Featured property categories"
              title="Curated commercial assets across Hyderabad."
              subtitle="Grade-A offices, high-street retail, fitted-out coworking floors, and mixed-use blocks — hand-shortlisted from our landlord network."
            />
          </Reveal>
          <Reveal delay={150}>
            <Link to="/properties" className="hidden md:inline-flex items-center gap-2 text-sm font-semibold text-[#144726] hover:text-[#C6A35B] transition-colors link-gold" data-testid="home-featured-view-all">
              View all properties <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>

        {/* Categories */}
        <div className="mt-12 grid grid-cols-2 gap-4 lg:grid-cols-4">
          {CATEGORIES.map((c, i) => (
            <Reveal key={c.key} delay={i * 70}>
              <Link to={`/properties?property_type=${c.key}`} className="group relative block aspect-[4/5] overflow-hidden rounded-xl" data-testid={`category-card-${c.key}`}>
                <img src={c.image} alt={c.label} className="h-full w-full object-cover transition-transform duration-[700ms] group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#144726] via-[#144726]/30 to-transparent" />
                <div className="absolute inset-x-4 bottom-4 flex items-end justify-between text-white">
                  <div>
                    <div className="flex h-9 w-9 items-center justify-center rounded-md bg-[#C6A35B] text-[#144726]">
                      <c.icon className="h-5 w-5" />
                    </div>
                    <p className="mt-3 font-display text-lg font-semibold leading-tight">{c.label}</p>
                  </div>
                  <ArrowRight className="h-5 w-5 text-[#C6A35B]" />
                </div>
              </Link>
            </Reveal>
          ))}
        </div>

        {/* Quick search */}
        <Reveal>
          <form onSubmit={onSearch} className="mt-12 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm md:p-6" data-testid="home-quick-search">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#C6A35B]">Find your next property</p>
            <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-12 md:items-center">
              <div className="md:col-span-3">
                <Input placeholder="Location (e.g. Gachibowli)" value={filters.location} onChange={(e) => setFilters({ ...filters, location: e.target.value })} data-testid="quick-search-location" />
              </div>
              <div className="md:col-span-3">
                <Select value={filters.property_type} onValueChange={(v) => setFilters({ ...filters, property_type: v })}>
                  <SelectTrigger data-testid="quick-search-type"><SelectValue placeholder="Property type" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All types</SelectItem>
                    <SelectItem value="office">Office</SelectItem>
                    <SelectItem value="retail">Retail</SelectItem>
                    <SelectItem value="coworking">Coworking</SelectItem>
                    <SelectItem value="mixed-use">Mixed-Use</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2">
                <Input type="number" min="0" placeholder="Min area (sqft)" value={filters.min_area} onChange={(e) => setFilters({ ...filters, min_area: e.target.value })} data-testid="quick-search-area" />
              </div>
              <div className="md:col-span-2">
                <Input type="number" min="0" placeholder="Max budget /sqft" value={filters.max_budget} onChange={(e) => setFilters({ ...filters, max_budget: e.target.value })} data-testid="quick-search-budget" />
              </div>
              <div className="md:col-span-2">
                <Button type="submit" className="w-full bg-[#144726] text-white hover:bg-[#0E3520]" data-testid="quick-search-submit">
                  <Search className="mr-2 h-4 w-4" /> Search
                </Button>
              </div>
            </div>
          </form>
        </Reveal>

        {/* Featured grid */}
        <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {loading && Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="animate-pulse rounded-xl border border-slate-200 bg-white">
              <div className="aspect-[16/10] w-full bg-slate-100" />
              <div className="space-y-2 p-5">
                <div className="h-5 w-3/4 rounded bg-slate-100" />
                <div className="h-4 w-1/2 rounded bg-slate-100" />
              </div>
            </div>
          ))}
          {!loading && properties.slice(0, 6).map((p) => (
            <PropertyCard key={p.id} property={p} testId={`featured-property-card`} />
          ))}
        </div>
      </div>
    </section>
  );
}
